import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, ArrowLeftRight, ChevronsUpDown, ChevronsDownUp } from 'lucide-react';
import useSplitLayout from '../../hooks/useSplitLayout';
import { moveSection, swapExpanded, swapOrder, toggleExpanded, visualOrder } from '../../utils/splitLayout';
import { useLanguage } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────────
// NotesTasksSplit — the body of "Notes & Task List" (#488 "Readjustment of notes
// and task"). It owns WHERE the two columns sit; the columns themselves are
// untouched and only receive three bits of header chrome to render.
//
//   * REORDER — drag a section by its module badge onto the other (native HTML5
//     DnD, the Boards column pattern #103/#138: a custom MIME, the whole section
//     as the drag image, the source dimmed, the target ringed), or pick "Move …"
//     in the column's kebab (keyboard / touch: mobile browsers don't do HTML5
//     DnD). PO review 2026-10-22: the visible header carries ONLY the expand
//     toggle and the kebab — the swap arrows live in the kebab, the grip is the
//     badge itself (no extra icon).
//     Sections render as a KEYED list, so a swap MOVES them — nothing remounts,
//     no list refetches, no draft is lost.
//   * EXPAND — the header's expand toggle makes that section fill the visible
//     height of the panel's scroller and paints it first; the other follows at
//     its normal (half) height BELOW the fold, reached by scrolling the panel
//     body (PanelShell's own `overflow-y-auto`). Nothing here ever sets
//     `overflow: hidden` — the body just grows taller than the scroller. While
//     expanded, drag-reorder is off but the kebab's swap stays (#544): it swaps
//     the two SLOTS — the other section takes the expanded top slot and the
//     previously expanded one goes below the fold — and the saved order follows
//     the new painted order, so collapsing keeps the swapped arrangement.
//
// `--split-fill` is the scroller's measured clientHeight (ResizeObserver); `dvh`
// is the fallback before the first measure and the cap, never `vh` (a mobile URL
// bar collapsing must not push the section edge around — #479).
// Both layout facts persist per user (utils/splitLayout.js).
// ─────────────────────────────────────────────────────────────────────────

const DRAG_MIME = 'application/x-omnis-split-section';
// Body padding (p-3) and gap (gap-3): an expanded section ends exactly one gap
// above the fold, so the next section's top edge IS the fold.
const PAD = 12;
const EXPANDED_MIN_PX = 320;
const SECTION_MIN_PX = 280;

const EXPANDED_STYLE = {
    height: `calc(var(--split-fill, 100dvh) - ${PAD * 2}px)`,
    maxHeight: `calc(100dvh - ${PAD * 2}px)`,
    minHeight: EXPANDED_MIN_PX,
};
// The collapsed section keeps the height it has in the 50/50 split.
const BELOW_FOLD_STYLE = {
    height: `calc((var(--split-fill, 100dvh) - ${PAD * 3}px) / 2)`,
    minHeight: SECTION_MIN_PX,
};

const NotesTasksSplit = ({ bodyRef, stacked, renderSection }) => {
    const { t } = useLanguage();
    const [layout, updateLayout] = useSplitLayout();
    const [dragging, setDragging] = useState(null);
    const [dropTarget, setDropTarget] = useState(null);
    const [fill, setFill] = useState(null);
    const draggingRef = useRef(null);
    const sectionRefs = useRef({});
    // A swap MOVES the section in the DOM, which can drop focus; put it back on
    // that section's kebab so a keyboard user can open it and move it again.
    const refocusRef = useRef(null);

    const expanded = layout.expanded;
    const vertical = stacked || !!expanded;
    const names = {
        notes: t('notesColumnTitle', 'Notes'),
        tasks: t('taskListTitle', 'Task List'),
    };

    // Measure the panel's scroller (PanelShell's body) so an expanded section
    // fills exactly what is visible.
    useEffect(() => {
        const scroller = bodyRef.current?.parentElement;
        if (!scroller || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => setFill(scroller.clientHeight || null));
        ro.observe(scroller);
        return () => ro.disconnect();
    }, [bodyRef]);

    useEffect(() => {
        const name = refocusRef.current;
        if (!name) return;
        refocusRef.current = null;
        sectionRefs.current[name]?.querySelector('[aria-haspopup="menu"]')?.focus();
    }, [layout.order, layout.expanded]);

    const swap = (name) => {
        refocusRef.current = name;
        updateLayout((l) => (l.expanded ? swapExpanded(l) : { ...l, order: swapOrder(l.order) }));
    };

    const toggleExpand = (name) => {
        const expanding = expanded !== name;
        updateLayout((l) => ({ ...l, expanded: toggleExpanded(l.expanded, name) }));
        // The expanded section is painted first: bring the scroller back to it.
        const scroller = bodyRef.current?.parentElement;
        if (expanding && scroller && typeof scroller.scrollTo === 'function') scroller.scrollTo({ top: 0 });
    };

    const endDrag = () => {
        draggingRef.current = null;
        setDragging(null);
        setDropTarget(null);
    };

    const dropOn = (target, e) => {
        e.preventDefault();
        const dragged = e.dataTransfer?.getData(DRAG_MIME) || draggingRef.current;
        endDrag();
        if (dragged) updateLayout((l) => ({ ...l, order: moveSection(l.order, dragged, target) }));
    };

    const chromeFor = (name) => {
        // Position as PAINTED: while expanded the expanded section is on top and
        // the layout is vertical, whatever the saved order says.
        const position = visualOrder(layout).indexOf(name);
        const isExpanded = expanded === name;
        const moveLabel = vertical
            ? (position === 0 ? t('splitMoveDown', 'Move {name} down', { name: names[name] }) : t('splitMoveUp', 'Move {name} up', { name: names[name] }))
            : (position === 0 ? t('splitMoveRight', 'Move {name} right', { name: names[name] }) : t('splitMoveLeft', 'Move {name} left', { name: names[name] }));
        const expandLabel = isExpanded
            ? t('splitCollapse', 'Collapse {name}', { name: names[name] })
            : t('splitExpand', 'Expand {name}', { name: names[name] });
        // Drag-reorder only while collapsed (see the header comment).
        // `dragProps` go on the column's module badge.
        const dragProps = expanded ? null : {
            'data-testid': 'split-section-drag-handle',
            'data-section': name,
            draggable: true,
            title: t('splitDragHandle', 'Drag to reorder'),
            onDragStart: (e) => {
                e.dataTransfer.setData(DRAG_MIME, name);
                e.dataTransfer.effectAllowed = 'move';
                // Drag the WHOLE section, not just the badge (Boards #138).
                const el = sectionRefs.current[name];
                if (el && typeof e.dataTransfer.setDragImage === 'function') {
                    try {
                        const r = el.getBoundingClientRect();
                        e.dataTransfer.setDragImage(el, e.clientX - r.left, e.clientY - r.top);
                    } catch { /* setDragImage unsupported — degrade gracefully */ }
                }
                draggingRef.current = name;
                setDragging(name);
            },
            onDragEnd: endDrag,
        };

        // The swap is a kebab item (KebabMenu shape), first in the menu — also
        // while expanded (#544).
        const menuItems = [{
            key: 'split-swap',
            testId: 'split-section-swap',
            section: name,
            label: moveLabel,
            icon: vertical ? <ArrowUpDown size={14} /> : <ArrowLeftRight size={14} />,
            onClick: () => swap(name),
        }];

        const layoutControls = (
            <button
                type="button"
                data-testid="split-section-expand"
                data-section={name}
                onClick={() => toggleExpand(name)}
                aria-pressed={isExpanded}
                aria-label={expandLabel}
                title={expandLabel}
                className="pai-icon-btn w-[30px] h-[30px] flex-shrink-0"
            >
                {isExpanded ? <ChevronsDownUp size={15} /> : <ChevronsUpDown size={15} />}
            </button>
        );

        return { dragProps, layoutControls, menuItems };
    };

    return (
        <div
            ref={bodyRef}
            data-testid="notes-tasks-split"
            data-order={layout.order.join(',')}
            data-expanded-section={expanded || ''}
            style={fill ? { '--split-fill': `${fill}px` } : undefined}
            className={`p-3 gap-3 flex ${vertical ? 'flex-col' : 'flex-row'} ${expanded ? 'min-h-full' : 'h-full min-h-0'}`}
        >
            {visualOrder(layout).map((name) => {
                const isExpanded = expanded === name;
                const style = !expanded ? undefined : (isExpanded ? EXPANDED_STYLE : BELOW_FOLD_STYLE);
                return (
                    <div
                        key={name}
                        ref={(el) => { sectionRefs.current[name] = el; }}
                        data-testid="split-section"
                        data-section={name}
                        data-position={layout.order.indexOf(name)}
                        data-expanded={isExpanded ? 'true' : 'false'}
                        data-dragging={dragging === name ? 'true' : 'false'}
                        data-drop-target={dropTarget === name ? 'true' : 'false'}
                        style={style}
                        onDragOver={(e) => {
                            if (!e.dataTransfer?.types?.includes(DRAG_MIME)) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            if (dropTarget !== name && draggingRef.current !== name) setDropTarget(name);
                        }}
                        onDragLeave={(e) => {
                            // Ignore leaves into our own children (header → list).
                            if (e.currentTarget.contains(e.relatedTarget)) return;
                            if (dropTarget === name) setDropTarget(null);
                        }}
                        onDrop={(e) => dropOn(name, e)}
                        className={`min-w-0 flex rounded-card transition-opacity ${expanded ? 'flex-none' : 'flex-1 min-h-0'} ${
                            dragging === name ? 'opacity-40' : ''
                        } ${dropTarget === name ? 'ring-2 ring-white/40' : ''}`}
                    >
                        {renderSection(name, chromeFor(name))}
                    </div>
                );
            })}
        </div>
    );
};

export default NotesTasksSplit;
